import { useEffect, useState } from "react";
import { ShareModes } from "../../enums/shareFlow";
import {
    bucketClassMemberActivities,
    resolveClassMemberActivityStatus,
    ClassMemberDashboardItemKinds,
    type BucketedClassMemberActivities,
    type ClassMemberDashboardItem,
} from "../../helpers/ClassMemberActivities";
import {
    getClassMemberActivitiesForClass,
    getTestResultForClassMember,
    getClassMemberVocabTrainingsForClass,
    type ClassMemberActivitiesSource,
} from "../../networking/tests";

interface UseClassMemberActivitiesResult extends BucketedClassMemberActivities {
    isLoading: boolean;
    hasError: boolean;
}

const EMPTY_BUCKETS: BucketedClassMemberActivities = { current: [], evaluations: [], older: [] };

/**
 * Loads a class's shared tests/practices and vocab trainings and buckets them into the dashboard
 * sections. For private classes (classMemberId) it also fetches the member's result per TEST to
 * detect submitted/evaluated states; public classes (classToken source) have no per-class-member state,
 * so every activity stays "new". A null source yields empty buckets without loading.
 */
export const useClassMemberActivities = (
    source: ClassMemberActivitiesSource | null,
    schoolClassId: string
): UseClassMemberActivitiesResult => {
    const [buckets, setBuckets] = useState<BucketedClassMemberActivities>(EMPTY_BUCKETS);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Extract primitives for the dependency array so an inline source object doesn't re-run the effect.
    const classMemberId = source && "classMemberId" in source ? source.classMemberId : undefined;
    const sourceSchoolClassId = source && "classToken" in source ? source.classToken : undefined;

    useEffect(() => {
        if (!classMemberId && !sourceSchoolClassId) {
            setBuckets(EMPTY_BUCKETS);
            setIsLoading(false);
            setHasError(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setHasError(false);

        (async () => {
            try {
                const activitySource: ClassMemberActivitiesSource = classMemberId
                    ? { classMemberId }
                    : { classToken: sourceSchoolClassId! };
                const [activities, vocabActivities] = await Promise.all([
                    getClassMemberActivitiesForClass(activitySource),
                    // A failing vocab lookup degrades to a tests-only dashboard instead of
                    // failing it entirely.
                    getClassMemberVocabTrainingsForClass(schoolClassId).catch((e) => {
                        console.warn("Failed to load class member vocab trainings", e);
                        return [];
                    }),
                ]);
                const testItems: ClassMemberDashboardItem[] = await Promise.all(
                    activities.map(async (activity) => {
                        // A single failing results lookup degrades that test to "new" instead of
                        // failing the whole dashboard.
                        const result =
                            classMemberId && activity.shareMode === ShareModes.TEST
                                ? await getTestResultForClassMember(activity.testId, classMemberId).catch(() => null)
                                : null;
                        return {
                            kind: ClassMemberDashboardItemKinds.TEST,
                            activity,
                            result,
                            status: resolveClassMemberActivityStatus(activity, result),
                        };
                    })
                );
                const vocabItems: ClassMemberDashboardItem[] = vocabActivities.map((activity) => ({
                    kind: ClassMemberDashboardItemKinds.VOCAB,
                    activity,
                }));
                if (cancelled) return;
                setBuckets(bucketClassMemberActivities([...testItems, ...vocabItems]));
            } catch (e) {
                if (cancelled) return;
                console.warn("Failed to load class member activities", e);
                setHasError(true);
                setBuckets(EMPTY_BUCKETS);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [classMemberId, sourceSchoolClassId, schoolClassId]);

    return { ...buckets, isLoading, hasError };
};
